/**
 * reconcile.js — the registry of items the scheduler CANNOT auto-resolve and that therefore must be
 * forced in front of a human (CT-025). Normal milestones get re-leveled automatically; these don't —
 * pinned hard dates, reactive items on an outside party's clock, chains blocked by an overdue
 * predecessor. Left alone they poison the plan (everything equalizes around a broken fact). The
 * Reconcile lane surfaces them with pre-canned one-tap shortcuts; once reconciled, the next nightly run
 * re-equalizes around the new truth.
 *
 * EXTENSIBLE BY DESIGN: a kind = one entry in RECONCILE_KINDS (look + pre-canned actions) + a detector
 * (normalize* here) + an executor branch (runReconcileAction in services/reconcile.js). The UI renders
 * whatever kinds appear — no UI change to add one. Pure + node-testable (no DOM, no fetch).
 */

// Each kind: display meta + the pre-canned actions offered on every item of that kind. Action `id`s are
// matched by the executor in services/reconcile.js; `open` is handled by the UI (navigate, no write).
export const RECONCILE_KINDS = {
  reactive_overdue: {
    label: 'Awaiting outside party',
    tag: 'Outside party',
    color: '#F5A623', bg: '#FDF3E2', text: '#9A6411',
    blurb: 'Past its expected date, waiting on an agency or client. The scheduler can’t move it — reconcile so the work behind it can re-level.',
    actions: [
      { id: 'complete', label: 'Close it out' },   // often it's actually done — guided closeout wizard
      { id: 'extend_2w', label: 'Extend 2 weeks' },
      { id: 'open', label: 'Open' },
    ],
  },
  // FUTURE (add an entry + a normalize* detector + an executor branch):
  //   pinned_impossible — Date Pinned but the committed date has passed → Unpin / Move pin / Protect
  //   overdue_thread    — open RFI/submittal/coordination past target → Extend / Close / Ping owner
  //   blocked_overdue   — blocked by an overdue or stalled predecessor → Nudge owner / Mark done
}

export function kindMeta(key) {
  return RECONCILE_KINDS[key] || { label: key, tag: key, color: '#8a9ba3', bg: 'rgba(7,39,45,0.06)', text: '#5a7884', actions: [] }
}

function first(v) { return Array.isArray(v) ? v[0] : v }
function ymd(v) { const s = first(v); return s ? String(s).slice(0, 10) : null }
function clean(v) { return String(first(v) || '').replace(/\*\*/g, '').trim() }

/**
 * Detector: raw reactive-overdue Project Deliverable records → reconcile items. Reactive = Days
 * Budgeted 0 (outside party's clock). Only ACTIVE (Executed) projects — On Hold / Cancelled / template
 * items are noise and must never pollute the lane.
 */
export function normalizeReactiveOverdue(records) {
  const out = []
  ;(records || []).forEach(r => {
    const f = r.fields || {}
    // Skip orphaned records — no deliverable type means no template, so the milestone won't
    // render in the outline and clicking Open lands on a blank "Milestone" zoom.
    if (!Array.isArray(f['Deliverable']) || f['Deliverable'].length === 0) return
    if (clean(f['Contract Stage (from Project)']) !== 'Executed') return
    const due = ymd(f['Latest Finish (LFT)'])
    out.push({
      id: 'reactive:' + r.id,
      kind: 'reactive_overdue',
      entityType: 'milestone',
      entityId: r.id,
      title: clean(f['Full Deliverable Name']) || clean(f['Name']) || 'Milestone',
      project: clean(f['Project Name']),
      projectId: first(f['Project']) || null,
      due,
      why: due ? 'expected back ' + due : 'past its expected date',
    })
  })
  return out.sort((a, b) => (a.due || '').localeCompare(b.due || ''))
}
